import { useRouter } from 'next/router'
import { useMemo } from 'react'
import SvgCMAAdd from '#/assets/images/icons/cma_add.svg'
import SvgCMANew from '#/assets/images/icons/cma_new.svg'
import SvgCompare from '#/assets/images/icons/compare_add.svg'
import SvgOpenWindow from '#/assets/images/icons/open-window.svg'
import SvgShare from '#/assets/images/icons/share.svg'
import SvgLike from '#/assets/images/workspace/heart.svg'
import SvgUnlike from '#/assets/images/workspace/heart_outline.svg'
import SvgHide from '#/assets/images/workspace/hidden.svg'
import SvgUnhide from '#/assets/images/workspace/hidden_outline.svg'
import AuthLock from '#/components/AuthLock'
import Button from '#/components/Button'
import Snackbar from '#/components/Snackbar'
import { logEvent } from '#/services/analytics'
import cn from '#/utils/classnames'
import { viewUrl } from '#/utils/properties'
import ReportAction from '../../ReportAction'
import { Section } from '../common'
import type { Props } from './types'

const Actions: React.FC<Props> = ({
  mode,
  property,
  isFavorite,
  isHidden,
  comparableIds,
  cmaData,
  user,
  addToList,
  removeFromList,
  shareLink,
  setComparableIds,
  setCMAData,
  showOutLinks
}) => {
  const btnClass = 'inline-flex text-2xs flex-col text-gray-500 bg-gray-200 text-xs rounded-md w-[4.25rem] h-[4.25rem] hover:bg-gray-200'
  const btnIconClass = 'mb-2 h-6'
  const [canAddToCMA, canStartNewCMA] = useMemo(() => {
    if (!cmaData || !showOutLinks) return [false, false]
    return [
      Boolean(cmaData.length && !cmaData.find(e => e.id === property.id)),
      Boolean(cmaData[0]?.id !== property.id)
    ]
  }, [cmaData])
  const router = useRouter()

  function onFavorite() {
    if (isFavorite) {
      removeFromList({
        propertyId: property.id,
        listId: 'favorites',
        workspaceId: 'default'
      })
    } else {
      isHidden && removeFromList({
        propertyId: property.id,
        listId: 'hidden',
        workspaceId: 'default'
      })
      addToList({
        propertyId: property.id,
        listId: 'favorites',
        workspaceId: 'default'
      })
    }

    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'favorite',
      value: property.id,
      address: property.address.FullAddress,
      update: isFavorite ? 'unfavorite' : 'favorite'
    })
  }

  function onHide() {
    if (isHidden) {
      removeFromList({
        propertyId: property.id,
        listId: 'hidden',
        workspaceId: 'default'
      })
    } else {
      isFavorite && removeFromList && removeFromList({
        propertyId: property.id,
        listId: 'favorites',
        workspaceId: 'default'
      })
      addToList({
        propertyId: property.id,
        listId: 'hidden',
        workspaceId: 'default'
      })
    }

    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'hidden',
      value: property.id,
      address: property.address.FullAddress,
      update: isHidden ? 'unhide' : 'hide'
    })
  }

  function onShare() {
    shareLink({
      type: 'Property',
      entityId: property.id,
      url: window.origin + viewUrl(property.id, property.address)
    })

    logEvent('share', {
      content_type: 'property_detail',
      item_id: property.id,
      value: property.address.FullAddress
    })
  }

  function onStartNewCMA() {
    setCMAData([{ id: property.id }])
    router.push('/cma')

    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'start_new_cma',
      value: property.id,
      address: property.address.FullAddress
    })
  }

  function onAddToCMA() {
    setCMAData([
      ...cmaData,
      { id: property.id }
    ])

    Snackbar.show({ message: 'The property has been added to CMA.' })

    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'add_to_cma',
      value: property.id,
      address: property.address.FullAddress
    })
  }

  function onCompare() {
    if (!comparableIds.includes(property.id)) {
      setComparableIds([...comparableIds, property.id])
    }

    Snackbar.show({ message: 'The property has been added to Compare.' })

    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'add_to_compare',
      value: property.id,
      address: property.address.FullAddress
    })
  }

  function onExpand() {
    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: 'page_mode',
      value: property.id,
      address: property.address.FullAddress
    })

    window.open(location.origin + viewUrl(property.id, property.address))
  }

  return (
    <Section mode='Carousel'>
      <Button className={btnClass} onClick={onFavorite}>
        {isFavorite ?
          <SvgLike className={cn(btnIconClass, 'text-red-500')} /> :
          <SvgUnlike className={btnIconClass} />}
        <span>Favorite</span>
      </Button>
      <Button className={btnClass} onClick={onShare}>
        <SvgShare className={btnIconClass} />
        <span>Share</span>
      </Button>
      {showOutLinks && (
        <ReportAction className={btnClass} property={property} user={user} />
      )}
      {false && showOutLinks && (
        <Button className={btnClass} onClick={onCompare}>
          <AuthLock
            disableSharedRole
            event='compare'
            onlyIcon
            requireRole='pro'
          />
          <SvgCompare className={btnIconClass} />
          <span>+ Compare</span>
        </Button>
      )}
      {false && canStartNewCMA && (
        <Button className={btnClass} onClick={onStartNewCMA}>
          <AuthLock
            disableSharedRole
            event='cma'
            onlyIcon
            requireRole='pro'
          />
          <SvgCMANew className={btnIconClass} />
          <span>New CMA</span>
        </Button>
      )}
      {false && canAddToCMA && (
        <Button className={btnClass} onClick={onAddToCMA}>
          <SvgCMAAdd className={btnIconClass} />
          <span>Add to CMA</span>
        </Button>
      )}
      <Button className={btnClass} onClick={onHide}>
        {isHidden ?
          <SvgHide className={cn(btnIconClass, 'text-gray-500')} /> :
          <SvgUnhide className={btnIconClass} />}
        <span>X-Out</span>
      </Button>
      {mode !== 'Page' && showOutLinks && (
        <Button className={`${btnClass} hidden xs:inline-flex`} onClick={onExpand}>
          <SvgOpenWindow className={btnIconClass} />
          <span>Open</span>
        </Button>
      )}
    </Section>
  )
}

export default Actions
